import { supabase }            from '../supabase.js';
import { pageQuery }           from '../lib/egress.js';
import { openSignatureModal }  from '../components/signature-pad.js';
import { generateRegisterPdf } from '../lib/register-pdf.js';
import { openStudentDrill }    from './drills.js';
import { canChange }           from '../lib/access.js';

/* ----- Date helpers ----- */
function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay() || 7;          // Mon=1 … Sun=7
  if (day !== 1) x.setDate(x.getDate() - (day - 1));
  x.setHours(0, 0, 0, 0);
  return x;
}
function isoDate(d) {
  // Use LOCAL date components — d.toISOString() strips a day for users east of UTC.
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtShort(d) { return d.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' }); }

/* ----- View state ----- */
const state = {
  classId:   null,
  weekStart: startOfWeek(new Date()),
  classes:   [],
  students:  [],
  rows:      [],     // attendance rows keyed by (student_id, session_date)
};

export async function renderAttendance() {
  const el = document.getElementById('view-attendance');
  const canMark = canChange('attendance');
  el.innerHTML = `
    <div class="page-header">
      <h1>Attendance</h1>
      <p>${canMark
        ? 'Mark daily attendance per class. Tap a cell to mark PRESENT or ABSENT.'
        : 'Daily attendance per class. View only — an admin can switch Attendance on for you in People &amp; rights.'}</p>
    </div>
    <div class="att-toolbar">
      <select id="att-class" class="select-input"></select>
      <div class="att-week-nav">
        <button class="filter-btn" id="att-prev">← Prev week</button>
        <button class="filter-btn" id="att-this">This week</button>
        <button class="filter-btn" id="att-next">Next week →</button>
        <span class="att-week-label" id="att-week-label"></span>
        <input type="date" id="att-date-jump" class="search-input"
               title="Jump to a specific date"
               style="padding:4px 8px;width:auto;font-size:.82rem">
      </div>
      <button class="btn btn-ghost${canMark ? '' : ' hidden'}"   id="att-capture-sigs" disabled title="Capture each learner's signature once — reused automatically when marking present">✍ Capture Signatures</button>
      <button class="btn btn-ghost${canMark ? '' : ' hidden'}"   id="att-log-cancel"   disabled title="Log that today's class was cancelled — keeps audit trail clean">📋 Log Class Cancellation</button>
      <button class="btn btn-ghost"   id="att-pdf-view" disabled>View register (PDF)</button>
      <button class="btn btn-primary" id="att-pdf" disabled>Download register (PDF)</button>
    </div>
    <div id="att-flags-panel"></div>
    <div id="att-grid-wrap" class="att-grid-wrap"></div>
  `;

  const { data: classes } = await pageQuery('classes', { order: 'class_name', ascending: true });
  state.classes = classes;

  const sel = document.getElementById('att-class');
  sel.innerHTML = `<option value="">— select a class —</option>` +
    classes.map(c => `<option value="${c.class_id}">${escape(c.class_name)} (cap ${c.capacity})</option>`).join('');
  sel.addEventListener('change', () => {
    state.classId = sel.value || null;
    const hasClass = !!state.classId;
    document.getElementById('att-pdf').disabled           = !hasClass;
    document.getElementById('att-pdf-view').disabled      = !hasClass;
    document.getElementById('att-capture-sigs').disabled  = !hasClass;
    document.getElementById('att-log-cancel').disabled    = !hasClass;
    refresh();
  });

  document.getElementById('att-prev').onclick = () => { state.weekStart = addDays(state.weekStart, -7); refresh(); };
  document.getElementById('att-next').onclick = () => { state.weekStart = addDays(state.weekStart,  7); refresh(); };
  document.getElementById('att-this').onclick = () => { state.weekStart = startOfWeek(new Date());     refresh(); };
  document.getElementById('att-date-jump').addEventListener('change', e => {
    if (!e.target.value) return;
    // Use noon local time to avoid DST / timezone edge cases
    state.weekStart = startOfWeek(new Date(e.target.value + 'T12:00:00'));
    e.target.value  = '';   // clear so it can be reused
    refresh();
  });

  document.getElementById('att-capture-sigs').onclick = () => openCaptureSignaturesFlow();
  document.getElementById('att-log-cancel').onclick   = async () => {
    const saved = await openCancellationModal();
    if (saved) await refresh();
  };

  document.getElementById('att-pdf-view').onclick = async () => {
    if (!state.classId) return;
    const btn = document.getElementById('att-pdf-view');
    btn.disabled = true; btn.textContent = 'Generating…';
    const previewWin = window.open('', '_blank');
    try {
      await generateRegisterPdf({ classId: state.classId, weekStart: state.weekStart, preview: true, previewWindow: previewWin });
    } catch (err) {
      previewWin?.close();
      alert(`PDF failed: ${err.message}`);
      console.error(err);
    } finally {
      btn.disabled = false; btn.textContent = 'View register (PDF)';
    }
  };

  document.getElementById('att-pdf').onclick = async () => {
    if (!state.classId) return;
    const btn = document.getElementById('att-pdf');
    btn.disabled = true; btn.textContent = 'Generating…';
    try {
      await generateRegisterPdf({ classId: state.classId, weekStart: state.weekStart });
    } catch (err) {
      alert(`PDF failed: ${err.message}`);
      console.error(err);
    } finally {
      btn.disabled = false; btn.textContent = 'Download register (PDF)';
    }
  };

  refresh();
}

async function refresh() {
  const wrap = document.getElementById('att-grid-wrap');
  const label = document.getElementById('att-week-label');
  const days = Array.from({ length: 5 }, (_, i) => addDays(state.weekStart, i));
  label.textContent = `Week of ${fmtShort(days[0])} – ${fmtShort(days[4])}`;

  if (!state.classId) {
    wrap.innerHTML = `<div class="att-empty">Pick a class above to see the weekly attendance grid.</div>`;
    document.getElementById('att-flags-panel').innerHTML = '';
    return;
  }

  wrap.innerHTML = `<div class="att-empty">Loading…</div>`;

  // Students enrolled in this class — alphabetical by first name then surname
  const { data: roster } = await supabase
    .from('class_roster')
    .select('enrollment_id,student_uuid,national_id,first_name,surname,programme_id')
    .eq('class_id', state.classId)
    .eq('enrollment_status', 'enrolled')
    .order('first_name', { ascending: true })
    .order('surname',    { ascending: true });
  state.students = roster ?? [];

  // Attendance rows for this class in this week
  const fromIso = isoDate(days[0]);
  const toIso   = isoDate(days[4]);
  const { data: rows } = await pageQuery('attendance', {
    select: 'attendance_id,student_uuid,session_date,status,signature_url,absence_document_url',
    eq:  { class_id: state.classId },
    gte: { session_date: fromIso },
    lte: { session_date: toIso }
  });

  // Index attendance by (student_uuid|date)
  const byKey = {};
  for (const r of rows) byKey[`${r.student_uuid}|${r.session_date}`] = r;
  state.rows = byKey;

  // Cancellations for this class this week
  const { data: cancels } = await supabase
    .from('class_cancellations')
    .select('cancellation_date, reason')
    .eq('class_id', state.classId)
    .gte('cancellation_date', fromIso)
    .lte('cancellation_date', toIso);
  const cancelledDates = new Set((cancels || []).map(c => c.cancellation_date));
  const cancelReason   = Object.fromEntries((cancels || []).map(c => [c.cancellation_date, c.reason]));

  if (!roster.length) {
    wrap.innerHTML = `<div class="att-empty">No active enrollments in this class.</div>`;
    document.getElementById('att-flags-panel').innerHTML = '';
    return;
  }

  wrap.innerHTML = `
    <table class="att-grid">
      <thead>
        <tr>
          <th class="att-name">Learner</th>
          ${days.map(d => {
            const iso = isoDate(d);
            return cancelledDates.has(iso)
              ? `<th class="att-col-cancelled" title="Class cancelled">📋 ${fmtShort(d)}</th>`
              : `<th>${fmtShort(d)}</th>`;
          }).join('')}
        </tr>
      </thead>
      <tbody>
        ${roster.map(s => {
          const fname = `${s.first_name || ''} ${s.surname || ''}`.trim();
          return `
          <tr>
            <td class="att-name att-name-clickable"
                data-student-uuid="${s.student_uuid}"
                data-student-name="${escape(fname)}"
                style="cursor:pointer"
                title="Open learner detail">
              <strong>${escape(s.first_name || '')} ${escape(s.surname || '')}</strong>
              <span class="att-id">${s.national_id}</span>
            </td>
            ${days.map(d => {
              const iso = isoDate(d);
              if (cancelledDates.has(iso)) {
                const label = (cancelReason[iso] || 'other').replace(/_/g, ' ');
                return `<td class="att-cell att-cell-cancelled" title="Cancelled: ${label}">📋 Cancelled</td>`;
              }
              const key = `${s.student_uuid}|${iso}`;
              const r   = byKey[key];
              return cellHtml(s, d, r);
            }).join('')}
          </tr>
        `;
        }).join('')}
      </tbody>
    </table>
  `;

  // Wire cell clicks — skip cancelled cells and paperclip icon; view only without Attendance
  const canMark = canChange('attendance');
  wrap.querySelectorAll('.att-cell').forEach(cell => {
    if (!canMark) { cell.style.cursor = 'default'; return; }
    if (cell.classList.contains('att-cell-cancelled')) return;
    cell.addEventListener('click', (e) => {
      if (e.target.classList.contains('att-doc-clip')) return;
      onCellClick(cell);
    });
  });

  // Wire paperclip icon → upload or view sick note
  wrap.querySelectorAll('.att-doc-clip').forEach(clip => {
    clip.addEventListener('click', (e) => {
      e.stopPropagation();
      onDocClipClick(clip);
    });
  });

  // Wire learner-name clicks → open student drill
  wrap.querySelectorAll('.att-name-clickable').forEach(td => {
    td.addEventListener('click', () => {
      openStudentDrill({ studentUuid: td.dataset.studentUuid, studentName: td.dataset.studentName });
    });
  });

  await refreshFlagsPanel();
}

function cellHtml(student, day, row) {
  const dateIso  = isoDate(day);
  const nameEsc  = escape((student.first_name || '') + ' ' + (student.surname || ''));
  if (!row) {
    return `<td class="att-cell att-cell-missing"
              data-student-uuid="${student.student_uuid}"
              data-date="${dateIso}"
              data-student-name="${nameEsc}">—</td>`;
  }
  const cls = row.status === 'PRESENT' ? 'att-cell-present'
            : row.status === 'ABSENT'  ? 'att-cell-absent'
            : 'att-cell-unmarked';
  let label = row.status === 'PRESENT' ? '✓ Present'
            : row.status === 'ABSENT'  ? '✗ Absent'
            : 'Unmarked';
  /* Paperclip icon for absent cells — filled if doc attached */
  if (row.status === 'ABSENT') {
    const clip = row.absence_document_url ? '📎' : '🖇';
    label += ` <span class="att-doc-clip"
                  data-attendance-id="${row.attendance_id}"
                  data-doc-url="${escape(row.absence_document_url || '')}"
                  title="${row.absence_document_url ? 'View / replace sick note' : 'Attach sick note'}"
                  style="cursor:pointer;font-size:.8em;opacity:.7">${clip}</span>`;
  }
  return `<td class="att-cell ${cls}"
            data-attendance-id="${row.attendance_id}"
            data-student-uuid="${student.student_uuid}"
            data-date="${dateIso}"
            data-student-name="${nameEsc}">${label}</td>`;
}

async function onCellClick(cell) {
  let attendanceId   = cell.dataset.attendanceId;
  const studentName  = cell.dataset.studentName;
  const dateLabel    = fmtShort(new Date(cell.dataset.date));

  const choice = await openMarkPrompt({ title: `${studentName} — ${dateLabel}`, hasRecord: !!attendanceId });
  if (!choice) return;

  // ---- Unmark: reset status + signature back to null so the cell is markable again ----
  if (choice === 'UNMARK') {
    const { error } = await supabase
      .from('attendance')
      .update({ status: null, signature_url: null })
      .eq('attendance_id', attendanceId);
    if (error) { alert(`Unmark failed: ${error.message}`); return; }
    await refresh();
    return;
  }

  if (!attendanceId) {
    const cls = state.classes.find(c => c.class_id === state.classId);
    const { data: ins, error: insErr } = await supabase
      .from('attendance')
      .insert({
        student_uuid: cell.dataset.studentUuid,
        class_id:     state.classId,
        programme_id: cls?.programme_id || null,
        session_date: cell.dataset.date,
        status:       null
      })
      .select('attendance_id')
      .single();
    if (insErr) { alert(`Couldn't create attendance row: ${insErr.message}`); return; }
    attendanceId = ins.attendance_id;
  }

  if (choice === 'PRESENT') {
    const cls         = state.classes.find(c => c.class_id === state.classId);
    const programmeId = cls?.programme_id || null;
    const studentUuid = cell.dataset.studentUuid;
    let   signatureUrl = null;

    /* ---- Check for stored per-term signature ---- */
    if (programmeId) {
      const { data: stored } = await supabase
        .from('student_signatures')
        .select('signature_url')
        .eq('student_uuid', studentUuid)
        .eq('programme_id', programmeId)
        .maybeSingle();
      if (stored?.signature_url) signatureUrl = stored.signature_url;
    }

    const { error } = await supabase.from('attendance')
      .update({ status: 'PRESENT', signature_url: signatureUrl })
      .eq('attendance_id', attendanceId);
    if (error) { alert(`Save failed: ${error.message}`); return; }
  } else {
    const { error } = await supabase.from('attendance')
      .update({ status: 'ABSENT', signature_url: null })
      .eq('attendance_id', attendanceId);
    if (error) { alert(`Save failed: ${error.message}`); return; }
  }

  await refresh();
}

/* ---------------------------------------------------------------
   Sick note / absence document upload
--------------------------------------------------------------- */
async function onDocClipClick(clip) {
  const attendanceId = clip.dataset.attendanceId;
  const existingUrl  = clip.dataset.docUrl;

  if (existingUrl) {
    const action = await openDocPrompt({ hasDoc: true });
    if (action === 'view')    { window.open(existingUrl, '_blank'); return; }
    if (action !== 'replace') return;
  }

  // File picker
  const input = document.createElement('input');
  input.type   = 'file';
  input.accept = 'application/pdf,image/jpeg,image/png,image/webp';
  input.click();
  input.addEventListener('change', async () => {
    const file = input.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('File must be under 10 MB.'); return; }

    const ext  = file.name.split('.').pop().toLowerCase();
    const { data: attRow } = await supabase.from('attendance')
      .select('class_id,student_uuid,session_date')
      .eq('attendance_id', attendanceId)
      .single();
    if (!attRow) { alert('Could not find attendance record.'); return; }

    const path = `attendance/${attRow.class_id}/${attRow.student_uuid}/${attRow.session_date}.${ext}`;
    const up   = await supabase.storage.from('documents').upload(path, file, { upsert: true, contentType: file.type });
    if (up.error) { alert(`Upload failed: ${up.error.message}`); return; }

    const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(path);
    const { error } = await supabase.from('attendance')
      .update({ absence_document_url: publicUrl })
      .eq('attendance_id', attendanceId);
    if (error) { alert(`Save failed: ${error.message}`); return; }

    await refresh();
  });
}

function openDocPrompt({ hasDoc }) {
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.className = 'sig-modal-overlay';
    overlay.innerHTML = `
      <div class="sig-modal sig-modal-narrow" role="dialog">
        <div class="sig-modal-head"><div class="sig-modal-title">Absence Document</div></div>
        <div class="sig-modal-body">A sick note is already attached.</div>
        <div class="sig-modal-actions">
          <button class="btn btn-ghost"    data-action="cancel">Cancel</button>
          <button class="btn btn-ghost"    data-action="view">View</button>
          <button class="btn btn-primary"  data-action="replace">Replace</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    overlay.addEventListener('click', e => {
      const a = e.target.dataset?.action;
      if (!a) return;
      overlay.remove();
      resolve(a === 'cancel' ? null : a);
    });
  });
}

/* ---------------------------------------------------------------
   Capture Signatures flow — shows a selection list first so the
   facilitator can choose which learners to sign, then iterates
   through only the selected ones.
--------------------------------------------------------------- */
async function openCaptureSignaturesFlow() {
  const cls = state.classes.find(c => c.class_id === state.classId);
  const programmeId = cls?.programme_id;
  if (!programmeId || !state.students.length) {
    alert('Select a class with enrolled students first.');
    return;
  }

  // Load who already has a signature for this programme
  const { data: existing } = await supabase
    .from('student_signatures')
    .select('student_uuid')
    .eq('programme_id', programmeId)
    .in('student_uuid', state.students.map(s => s.student_uuid));
  const alreadySigned = new Set((existing || []).map(r => r.student_uuid));

  // Let the facilitator choose which students to capture
  const selected = await openSigSelectionModal({
    students:     state.students,
    alreadySigned,
    className:    cls?.class_name || 'Class',
  });
  if (!selected || !selected.length) return;

  const { data: { user } } = await supabase.auth.getUser();
  const capturedBy = user?.id ?? null;

  // Iterate through only the selected students
  let captured = 0;
  for (let i = 0; i < selected.length; i++) {
    const student  = selected[i];
    const fullName = `${student.first_name || ''} ${student.surname || ''}`.trim();
    const hasSig   = alreadySigned.has(student.student_uuid);
    const title    = `${i + 1} / ${selected.length}  ·  ${fullName}${hasSig ? '  (replacing existing)' : ''}`;

    const blob = await openSignatureModal({ title });
    if (!blob) continue;   // user pressed Cancel on pad — skip, keep going

    const termPath = `terms/${programmeId}/${student.student_uuid}.png`;
    const up = await supabase.storage.from('signatures').upload(termPath, blob, { upsert: true, contentType: 'image/png' });
    if (up.error) {
      if (!confirm(`Upload failed for ${fullName}. Skip and continue?`)) break;
      continue;
    }
    const { data: { publicUrl } } = supabase.storage.from('signatures').getPublicUrl(termPath);
    await supabase.from('student_signatures').upsert(
      { student_uuid: student.student_uuid, programme_id: programmeId, signature_url: publicUrl, captured_by: capturedBy },
      { onConflict: 'student_uuid,programme_id' }
    );
    alreadySigned.add(student.student_uuid);
    captured++;
  }
  if (captured > 0) {
    alert(`✓ Done! ${captured} signature${captured === 1 ? '' : 's'} captured.`);
  }
}

/* ---------------------------------------------------------------
   Selection modal — lets the facilitator pick which learners to
   sign for before the signature pad opens.
--------------------------------------------------------------- */
function openSigSelectionModal({ students, alreadySigned, className }) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'sig-modal-overlay';
    overlay.innerHTML = `
      <div class="sig-modal" style="max-width:460px;width:92vw;max-height:86vh;display:flex;flex-direction:column">
        <div class="sig-modal-head" style="display:flex;justify-content:space-between;align-items:center">
          <div class="sig-modal-title">✍ Capture Signatures</div>
          <button id="sig-sel-close" style="background:none;border:none;font-size:1.3rem;line-height:1;cursor:pointer;color:var(--text-muted);padding:0 2px" title="Close">✕</button>
        </div>
        <div style="padding:8px 20px 10px;font-size:.8rem;color:var(--text-muted);border-bottom:1px solid var(--border-light);display:flex;justify-content:space-between;align-items:center">
          <span>${escape(className)}</span>
          <button id="sig-sel-toggle" class="btn btn-ghost" style="font-size:.75rem;padding:2px 10px">Select All</button>
        </div>
        <div id="sig-sel-list" style="overflow-y:auto;flex:1;padding:4px 0">
          ${students.map(s => {
            const fullName = `${s.first_name || ''} ${s.surname || ''}`.trim();
            const hasSig   = alreadySigned.has(s.student_uuid);
            return `
              <label style="display:flex;align-items:center;gap:10px;padding:9px 20px;cursor:pointer;border-bottom:1px solid var(--border-light);font-size:.85rem">
                <input type="checkbox" class="sig-sel-chk" data-uuid="${escape(s.student_uuid)}"
                       style="width:15px;height:15px;flex-shrink:0">
                <span style="flex:1">${escape(fullName)}</span>
                ${hasSig
                  ? '<span style="font-size:.72rem;color:var(--green);font-weight:600;white-space:nowrap">✓ signed</span>'
                  : '<span style="font-size:.72rem;color:var(--text-muted);white-space:nowrap">no signature</span>'}
              </label>`;
          }).join('')}
        </div>
        <div style="padding:12px 20px;border-top:1px solid var(--border-light);display:flex;justify-content:flex-end;gap:8px">
          <button id="sig-sel-cancel" class="btn btn-ghost">Cancel</button>
          <button id="sig-sel-start"  class="btn btn-primary" disabled>
            Capture selected (<span id="sig-sel-count">0</span>)
          </button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const chks      = overlay.querySelectorAll('.sig-sel-chk');
    const countEl   = overlay.querySelector('#sig-sel-count');
    const startBtn  = overlay.querySelector('#sig-sel-start');
    const toggleBtn = overlay.querySelector('#sig-sel-toggle');

    function updateCount() {
      const n       = [...chks].filter(c => c.checked).length;
      countEl.textContent  = n;
      startBtn.disabled    = n === 0;
      toggleBtn.textContent = n === chks.length ? 'Deselect All' : 'Select All';
    }

    chks.forEach(c => c.addEventListener('change', updateCount));

    toggleBtn.addEventListener('click', () => {
      const allChecked = [...chks].every(c => c.checked);
      chks.forEach(c => { c.checked = !allChecked; });
      updateCount();
    });

    function close() { overlay.remove(); resolve(null); }
    overlay.querySelector('#sig-sel-close').addEventListener('click',  close);
    overlay.querySelector('#sig-sel-cancel').addEventListener('click', close);

    startBtn.addEventListener('click', () => {
      const uuids    = new Set([...chks].filter(c => c.checked).map(c => c.dataset.uuid));
      const selected = students.filter(s => uuids.has(s.student_uuid));
      overlay.remove();
      resolve(selected);
    });
  });
}

function openMarkPrompt({ title, hasRecord = false }) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'sig-modal-overlay';
    overlay.innerHTML = `
      <div class="sig-modal sig-modal-narrow" role="dialog" aria-modal="true">
        <div class="sig-modal-head"><div class="sig-modal-title">${title}</div></div>
        <div class="sig-modal-body">Mark attendance:</div>
        <div class="sig-modal-actions">
          <button class="btn btn-ghost"   data-action="cancel">Cancel</button>
          ${hasRecord ? `<button class="btn btn-ghost" data-action="UNMARK" title="Reset to unmarked so it can be marked again">↩ Unmark</button>` : ''}
          <button class="btn btn-danger"  data-action="ABSENT">Absent</button>
          <button class="btn btn-primary" data-action="PRESENT">Present</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    overlay.addEventListener('click', (e) => {
      const action = e.target.dataset?.action;
      if (!action) return;
      overlay.remove();
      resolve(action === 'cancel' ? null : action);
    });
  });
}

/* ---------------------------------------------------------------
   Dropout flags panel — shown below the grid when open flags exist
--------------------------------------------------------------- */
async function refreshFlagsPanel() {
  const panel = document.getElementById('att-flags-panel');
  if (!panel || !state.classId) { if (panel) panel.innerHTML = ''; return; }

  const { data: flags } = await supabase
    .from('dropout_flags')
    .select('flag_id,student_uuid,streak_start_date,flagged_at,status')
    .eq('class_id', state.classId)
    .eq('status', 'open')
    .order('flagged_at', { ascending: true });

  if (!flags?.length) { panel.innerHTML = ''; return; }

  const nameOf = {};
  for (const s of state.students) nameOf[s.student_uuid] = `${s.first_name || ''} ${s.surname || ''}`.trim();

  const daysSince = iso => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

  panel.innerHTML = `
    <div class="flags-panel">
      <div class="flags-panel-header">
        ⚠️ ${flags.length} learner${flags.length > 1 ? 's' : ''} flagged — 3 consecutive unexplained absences
      </div>
      <div class="flags-panel-body">
        ${flags.map(f => {
          const name  = escape(nameOf[f.student_uuid] || 'Unknown learner');
          const days  = daysSince(f.flagged_at);
          const stale = days >= 1;
          return `
          <div class="flag-row${stale ? ' flag-row-stale' : ''}" data-flag-id="${f.flag_id}">
            <div class="flag-info">
              <strong>${name}</strong>
              <span class="flag-meta">
                Absent since ${f.streak_start_date}
                &middot; Flagged ${days === 0 ? 'today' : days + 'd ago'}
                ${stale ? '<span class="flag-stale-badge">No update yet</span>' : ''}
              </span>
            </div>
            <div class="flag-action">
              <select class="select-input flag-status-select" data-flag-id="${f.flag_id}" ${canChange('attendance') ? '' : 'disabled'}
                      style="font-size:.8rem;padding:4px 8px;min-width:220px">
                <option value="">— update status —</option>
                <option value="returning">Contacted — learner returning</option>
                <option value="confirmed_dropout">Contacted — confirmed dropout</option>
                <option value="sick_note_incoming">Sick note incoming</option>
                <option value="unreachable">Unreachable — escalate to ops</option>
              </select>
            </div>
          </div>`;
        }).join('')}
      </div>
    </div>`;

  panel.querySelectorAll('.flag-status-select').forEach(sel => {
    sel.addEventListener('change', async () => {
      const newStatus = sel.value;
      if (!newStatus) return;
      sel.disabled = true;
      await updateFlagStatus(sel.dataset.flagId, newStatus);
      await refreshFlagsPanel();
    });
  });
}

async function updateFlagStatus(flagId, newStatus) {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase
    .from('dropout_flags')
    .update({
      status:            newStatus,
      status_updated_at: new Date().toISOString(),
      status_updated_by: user?.id ?? null,
    })
    .eq('flag_id', flagId);
  if (error) alert(`Failed to update flag: ${error.message}`);
}

/* ---------------------------------------------------------------
   Class cancellation modal
--------------------------------------------------------------- */
function openCancellationModal() {
  const todayIso = isoDate(new Date());
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.className = 'sig-modal-overlay';
    overlay.innerHTML = `
      <div class="sig-modal" style="max-width:420px;width:92vw">
        <div class="sig-modal-head">
          <div class="sig-modal-title">📋 Log Class Cancellation</div>
        </div>
        <div class="sig-modal-body" style="display:flex;flex-direction:column;gap:14px">
          <label style="display:flex;flex-direction:column;gap:4px;font-size:.85rem">
            Date
            <input type="date" id="cancel-date" class="search-input"
                   value="${todayIso}" max="${todayIso}" style="font-size:.85rem">
          </label>
          <label style="display:flex;flex-direction:column;gap:4px;font-size:.85rem">
            Reason <span style="color:var(--red)">*</span>
            <select id="cancel-reason" class="select-input" style="font-size:.85rem">
              <option value="">— select reason —</option>
              <option value="facilitator_sick">Facilitator sick</option>
              <option value="facilitator_on_leave">Facilitator on leave</option>
              <option value="venue_unavailable">Venue unavailable</option>
              <option value="public_holiday">Public holiday</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label style="display:flex;flex-direction:column;gap:4px;font-size:.85rem">
            Notes (optional)
            <textarea id="cancel-notes" class="search-input" rows="2"
                      style="resize:vertical;font-size:.85rem;font-family:inherit"
                      placeholder="Any additional context…"></textarea>
          </label>
        </div>
        <div class="sig-modal-actions">
          <button class="btn btn-ghost"    id="cancel-modal-dismiss">Cancel</button>
          <button class="btn btn-primary"  id="cancel-modal-submit">Log Cancellation</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    overlay.querySelector('#cancel-modal-dismiss').addEventListener('click', () => {
      overlay.remove(); resolve(null);
    });

    overlay.querySelector('#cancel-modal-submit').addEventListener('click', async () => {
      const date   = overlay.querySelector('#cancel-date').value;
      const reason = overlay.querySelector('#cancel-reason').value;
      const notes  = overlay.querySelector('#cancel-notes').value.trim();
      if (!reason) { alert('Please select a reason.'); return; }

      const btn = overlay.querySelector('#cancel-modal-submit');
      btn.disabled = true; btn.textContent = 'Saving…';

      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('class_cancellations')
        .upsert(
          { class_id: state.classId, cancellation_date: date, reason, notes: notes || null, logged_by: user?.id ?? null },
          { onConflict: 'class_id,cancellation_date' }
        );

      if (error) {
        alert(`Failed to log cancellation: ${error.message}`);
        btn.disabled = false; btn.textContent = 'Log Cancellation';
        return;
      }
      overlay.remove(); resolve(true);
    });
  });
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
